// NPM packages
import React from 'react';
import { Typography, useTheme } from '@mui/material';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router';

// All other imports
import PageContent from 'components/core/PageContent';
import { useServices } from 'services';
import useSelectProjectByUrl from 'hooks/useSelectProjectByUrl';
import useResourceGuard from 'hooks/useResourceGuard';
import { observer } from 'mobx-react-lite';
import Project from 'types/Project';
import ProjectForm from 'components/project/ProjectForm';
import Link from 'components/core/Link';
import EditLogo from 'components/project/EditLogo';

const Root = styled(PageContent)({
  padding:'0!important;',
});

const Flexy = styled.div({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  columnCap: '1rem',
  rowGap: '4rem',
});


const LogoContainer = styled.div({
  flex: '0 1 auto',
  width: '100%',
  overflow: 'hidden',
  paddingLeft: '2.5rem',
  maxWidth: '384px',
});
const Logo = styled.img({
  width: '100%',
  marginBottom: '1rem',
});

export interface EditProjectProps {
  className?: string;
}

function EditProject(props: EditProjectProps): React.ReactElement | null {
  const { className } = props;
  const { projectService } = useServices();
  useSelectProjectByUrl({ projectService });
  const spinner = useResourceGuard({
    resource: projectService.selectedProject,
  });
  const navigate = useNavigate();
  const theme = useTheme();
  let body: React.ReactNode;
  if (spinner) {
    body = spinner;
  } else {
    const project = projectService.selectedProject as Project;
    body = (
      <Flexy>
        <div style={{maxWidth: theme.maxTextWidth}}>
          <ProjectForm
            initialValues={{
              title: project.title,
              subtitle: project.subtitle,
              description: project.description,
              longDescription: project.longDescription,
              resources: project.resources,
              contact: project.contact,
            }}
            onSubmit={(data) =>
              project.updateMetadata({
                title: data.title,
                subtitle: data.subtitle,
                description: data.description,
                longDescription: data.longDescription,
                resources: data.resources,
                contact: data.contact,
              })
            }
            getSuccessMessage={() => (
              <Typography>
                Success! Go to{' '}
                <Link to={`/projects/${project.slug}`}>{project.title}</Link> to
                review your changes.
              </Typography>
            )}
            getErrorMessage={(rejectedVal) =>
              rejectedVal?.message ?? 'Failed to update project'
            }
            onCancel={() =>
              navigate(`/projects/${project.slug}`, { replace: true })
            }
          />
        </div>
        <LogoContainer>
          <Logo src={project.links.logo} alt="" />
          <EditLogo project={project} />
        </LogoContainer>
      </Flexy>
    );
  }
  return (
    <Root className={className} maxWidth={theme.maxToolWidth}>
      <Typography variant="h1">Edit Project</Typography>
      {body}
    </Root>
  );
}

export default observer(EditProject);
